import { useState } from "react";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/format";
import { Landmark, CheckCircle2, AlertCircle, ExternalLink, Loader2 } from "lucide-react";

const Sum = ({ label, value, cls, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl shadow-card border border-slate-100 p-5">
    <p className="text-sm text-slate-500">{label}</p>
    <p className={`font-heading text-2xl font-bold mt-1 ${cls}`}>{money(value)}</p>
  </div>
);

function BankConnectCard({ connect, connectBank, openBankDashboard }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!connect) return null;
  const run = (fn) => async () => { setBusy(true); try { await fn(); } finally { setBusy(false); } };

  if (!connect.configured) {
    return (
      <div data-testid="bank-connect-card" className="bg-white rounded-2xl shadow-card border border-slate-100 p-5 flex items-start gap-4">
        <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-100 text-slate-500 shrink-0"><Landmark size={20} /></span>
        <div>
          <h3 className="font-heading font-semibold text-slate-900">{t("hostPayouts.achTitle")}</h3>
          <p className="text-slate-500 text-sm mt-1">{t("hostPayouts.achSetup")}</p>
        </div>
      </div>
    );
  }

  const active = connect.connected && connect.payouts_enabled;
  const pending = connect.connected && !connect.payouts_enabled;

  return (
    <div data-testid="bank-connect-card" className={`rounded-2xl shadow-card border p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${active ? "bg-emerald-50 border-emerald-100" : pending ? "bg-amber-50 border-amber-100" : "bg-white border-slate-100"}`}>
      <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0 ${active ? "bg-emerald-100 text-emerald-600" : pending ? "bg-amber-100 text-amber-600" : "bg-blue-50 text-blue-600"}`}>
        {active ? <CheckCircle2 size={20} /> : pending ? <AlertCircle size={20} /> : <Landmark size={20} />}
      </span>
      <div className="flex-1">
        <h3 className="font-heading font-semibold text-slate-900">
          {active ? t("hostPayouts.connectedEnabled") : pending ? t("hostPayouts.finishConnecting") : t("hostPayouts.getPaidDirect")}
        </h3>
        <p className="text-slate-500 text-sm mt-1">
          {active ? t("hostPayouts.connectedDesc") : pending ? t("hostPayouts.pendingDesc") : t("hostPayouts.connectDesc")}
        </p>
      </div>
      <div className="shrink-0">
        {active ? (
          <button onClick={run(openBankDashboard)} disabled={busy} data-testid="manage-bank-btn"
            className="flex items-center gap-2 border-2 border-emerald-200 bg-white px-5 py-2.5 rounded-full text-sm font-semibold text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />} {t("hostPayouts.manageBank")}
          </button>
        ) : (
          <button onClick={run(connectBank)} disabled={busy} data-testid="connect-bank-btn"
            className="flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Landmark size={15} />} {pending ? t("hostPayouts.finishSetup") : t("hostPayouts.connectBank")}
          </button>
        )}
      </div>
    </div>
  );
}

export function HostPayouts({ earnings, connect, connectBank, openBankDashboard }) {
  const { t } = useTranslation();
  const STATE = {
    payable: { label: t("hostPayouts.ready"), cls: "bg-green-100 text-green-700" },
    paid: { label: t("hostPayouts.paid"), cls: "bg-blue-100 text-blue-700" },
  };
  const stateInfo = (s) => STATE[s] || { label: t("hostPayouts.pendingStatus"), cls: "bg-amber-100 text-amber-700" };
  return (
    <div className="space-y-4" data-testid="host-payouts">
      <BankConnectCard connect={connect} connectBank={connectBank} openBankDashboard={openBankDashboard} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Sum testid="payout-ready" label={t("hostPayouts.readyToPayOut")} value={earnings.ready} cls="text-green-600" />
        <Sum testid="payout-pending" label={t("hostPayouts.pending")} value={earnings.pending} cls="text-amber-600" />
        <Sum testid="payout-paid" label={t("hostPayouts.paidOut")} value={earnings.paid} cls="text-blue-600" />
      </div>
      <div className="bg-blue-50/60 border border-blue-100 rounded-2xl p-4 text-sm text-slate-600" data-testid="payout-formula">
        <b className="text-slate-900">How your payout is calculated:</b> you keep <b>100%</b> of the rental rate, the delivery fee, and the young-driver fee (when applicable). Belo charges the guest a separate service fee, so there's no deduction from your earnings. Sales tax and the refundable deposit are not part of your payout. Payouts release after the trip ends and the car is returned, with no open claims.
      </div>
      <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-x-auto">
        {earnings.rows.length === 0 ? (
          <p className="p-8 text-center text-slate-400" data-testid="payouts-empty">{t("hostPayouts.empty")}</p>
        ) : (
          <table className="w-full text-sm min-w-[640px]" data-testid="payouts-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{t("hostPayouts.car")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("hostPayouts.guest")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("hostPayouts.dates")}</th>
                <th className="text-right px-5 py-3 font-semibold">{t("hostPayouts.trip")}</th>
                <th className="text-right px-5 py-3 font-semibold">{t("hostPayouts.yourPayout")}</th>
                <th className="text-right px-5 py-3 font-semibold">{t("hostPayouts.status")}</th>
              </tr>
            </thead>
            <tbody>
              {earnings.rows.map((r) => {
                const s = stateInfo(r.state);
                return (
                  <tr key={r.booking_id} data-testid={`payout-row-${r.booking_id}`} className="border-t border-slate-100">
                    <td className="px-5 py-3 font-medium text-slate-900">{r.car_name}</td>
                    <td className="px-5 py-3 text-slate-600">{r.guest}</td>
                    <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{r.pickup_date} → {r.return_date}</td>
                    <td className="px-5 py-3 text-right text-slate-600">{money(r.trip_price)}</td>
                    <td className="px-5 py-3 text-right font-semibold text-slate-900">{money(r.host_payout)}</td>
                    <td className="px-5 py-3 text-right"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>{s.label}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
